// HTTP-клиенты auth и engine на openapi-fetch с типами из OpenAPI.
// Access-токен живёт в памяти, refresh в httpOnly cookie (web). При 401 один раз
// обновляем токен и повторяем запрос; если обновить не удалось, выходим.
import createClient, { type Middleware } from "openapi-fetch";
import type { paths as AuthPaths } from "./auth.gen";
import type { paths as EnginePaths } from "./engine.gen";
import type { paths as NotifyPaths } from "./notifications.gen";

export const APP_VERSION = "0.1.0";

export interface ApiErrorBody {
  error: string;
  message: string;
  details?: { code?: string; message?: string; [k: string]: unknown }[];
}

export class ApiError extends Error {
  status: number;
  code: string;
  details: NonNullable<ApiErrorBody["details"]>;

  constructor(status: number, body: Partial<ApiErrorBody> | null) {
    super(body?.message ?? `Ошибка ${status}`);
    this.status = status;
    this.code = body?.error ?? (status === 0 ? "network" : `http_${status}`);
    this.details = body?.details ?? [];
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
let onSessionLost: () => void = () => {};

export const tokenStore = {
  get: () => accessToken,
  set: (t: string | null) => {
    accessToken = t;
  },
  onSessionLost: (cb: () => void) => {
    onSessionLost = cb;
  },
};

const baseHeaders = { "X-Client": "web", "X-Client-Version": APP_VERSION };

/** Обновление access по cookie. Один запрос на все одновременные 401. */
export async function refreshAccess(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const r = await fetch("/api/v1/auth/refresh", {
          method: "POST",
          credentials: "include",
          headers: { ...baseHeaders, "Content-Type": "application/json" },
          body: "{}",
        });
        if (!r.ok) return false;
        const data = (await r.json()) as { access_token: string };
        accessToken = data.access_token;
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

const NO_RETRY = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/register"];

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    Object.entries(baseHeaders).forEach(([k, v]) => request.headers.set(k, v));
    if (accessToken) request.headers.set("Authorization", `Bearer ${accessToken}`);
    return request;
  },
  async onResponse({ request, response }) {
    const path = new URL(request.url).pathname;
    if (response.status === 401 && !NO_RETRY.some((p) => path.endsWith(p))) {
      if (await refreshAccess()) {
        const retry = new Request(request, { headers: new Headers(request.headers) });
        retry.headers.set("Authorization", `Bearer ${accessToken}`);
        return fetch(retry);
      }
      onSessionLost();
    }
    return response;
  },
};

// baseUrl = origin: в браузере то же самое, а в тестах (Node) Request требует абсолютный URL
const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

// fetch берём лениво, чтобы тесты могли подменить globalThis.fetch после импорта
const lazyFetch = (req: Request) => globalThis.fetch(req);

export const authApi = createClient<AuthPaths>({
  baseUrl,
  credentials: "include",
  fetch: lazyFetch,
});
authApi.use(authMiddleware);

export const engineApi = createClient<EnginePaths>({ baseUrl, fetch: lazyFetch });
engineApi.use(authMiddleware);

/** Сервис notifications: подключение Telegram */
export const notifyApi = createClient<NotifyPaths>({ baseUrl, fetch: lazyFetch });
notifyApi.use(authMiddleware);

/** Превращает ответ openapi-fetch в данные или бросает ApiError */
export function unwrap<T>(res: { data?: T; error?: unknown; response: Response }): T {
  if (res.response.ok && res.data !== undefined) return res.data;
  if (res.response.ok) return undefined as T;
  const err = res.error as Partial<ApiErrorBody> | { detail?: unknown } | null;
  if (err && "detail" in err && Array.isArray(err.detail)) {
    const first = err.detail[0] as { msg?: string; loc?: unknown[] } | undefined;
    throw new ApiError(res.response.status, {
      error: "validation_error",
      message: first?.msg
        ? `${(first.loc ?? []).slice(-1)[0] ?? ""}: ${first.msg}`
        : "Проверьте поля",
    });
  }
  throw new ApiError(res.response.status, err as Partial<ApiErrorBody> | null);
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Что-то пошло не так";
}

export type Schemas = EnginePaths extends never
  ? never
  : import("./engine.gen").components["schemas"];
export type AuthSchemas = import("./auth.gen").components["schemas"];
